import { z } from 'zod'

import { asArray, isDefined } from '@mntn-dev/utilities'
import {
  EnumBuilder,
  type NonEmptyArray,
  type SingleOrArray,
  type ZodInfer,
} from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import { BillingScheduleSchema } from './billing/billing-schedule.ts'
import {
  ChargeMethodSchema,
  NoneableChargeMethodSchema,
} from './billing/charge-method.ts'
import { CurrencySchema } from './billing/currency.ts'
import { InvoiceBillingTermsSchema } from './billing/invoice-terms.ts'
import {
  PackageFormatSchema,
  PackagePayoutSchema,
  type PackageSource,
  PackageSourceSchema,
  PackageTranslationsSchema,
} from './package.models.ts'
import {
  CompanyNameSchema,
  CostMarginPercentSchema,
  DataOriginSchema,
  DateSchema,
  DescriptionSchema,
  DollarsSchema,
  NameSchema,
  NonEmptyStringSchema,
  TimestampSchema,
} from './property.models.ts'
import { ReviewRoundCountSchema } from './review.models.ts'
import { TagFilterSchema } from './tag.models.ts'
import {
  FileIdSchema,
  OrganizationIdSchema,
  PackageIdSchema,
  ProjectIdSchema,
  ProjectKeySchema,
  ProjectUrnSchema,
  TeamIdSchema,
  UserIdSchema,
} from './unique-id.models.ts'

/**
 * ProjectStatus
 */
export const [ProjectStatuses, ProjectStatusSchema, ProjectStatusEnum] =
  EnumBuilder(
    'draft',
    'archived',
    'bidding_open',
    'processing_bids',
    'bidding_closed',
    'awarded',
    'pre_production',
    'pre_production_complete',
    'production',
    'post_production',
    'post_production_complete',
    'processing_final_files',
    'processing_final_files_complete',
    'complete',
    'closed'
  )
export type ProjectStatus = ZodInfer<typeof ProjectStatusSchema>

export const ActiveProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ProjectStatusEnum.draft,
  ProjectStatusEnum.bidding_open,
  ProjectStatusEnum.processing_bids,
  ProjectStatusEnum.bidding_closed,
  ProjectStatusEnum.awarded,
  ProjectStatusEnum.pre_production,
  ProjectStatusEnum.pre_production_complete,
  ProjectStatusEnum.production,
  ProjectStatusEnum.post_production,
  ProjectStatusEnum.post_production_complete,
  ProjectStatusEnum.processing_final_files,
  ProjectStatusEnum.processing_final_files_complete,
]

export const SubmittedProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ProjectStatusEnum.bidding_open,
  ProjectStatusEnum.processing_bids,
  ProjectStatusEnum.bidding_closed,
  ProjectStatusEnum.awarded,
]

export const InProgressProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ProjectStatusEnum.pre_production,
  ProjectStatusEnum.pre_production_complete,
  ProjectStatusEnum.production,
  ProjectStatusEnum.post_production,
  ProjectStatusEnum.post_production_complete,
]

export const InProductionProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ProjectStatusEnum.pre_production,
  ProjectStatusEnum.pre_production_complete,
  ProjectStatusEnum.production,
  ProjectStatusEnum.post_production,
]

export const BiddingProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ProjectStatusEnum.bidding_open,
  ProjectStatusEnum.processing_bids,
  ProjectStatusEnum.bidding_closed,
]

/* active from the perspective of the agency for bid penalty calculation */
export const AgencyActiveProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ...InProgressProjectStatuses,
  ProjectStatusEnum.processing_final_files,
  ProjectStatusEnum.processing_final_files_complete,
]

export const AgencyHighlightedProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ...BiddingProjectStatuses,
]

export const BrandHighlightedProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ...AgencyHighlightedProjectStatuses,
  ProjectStatusEnum.awarded,
]

export const CompletedProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ProjectStatusEnum.complete,
]

export const CloseableProjectStatuses: NonEmptyArray<ProjectStatus> = [
  ProjectStatusEnum.awarded,
  ProjectStatusEnum.pre_production,
  ProjectStatusEnum.pre_production_complete,
  ProjectStatusEnum.production,
  ProjectStatusEnum.post_production,
  ProjectStatusEnum.post_production_complete,
  ProjectStatusEnum.processing_final_files,
  ProjectStatusEnum.processing_final_files_complete,
]

export const AwardedProjectStatusSchema = ProjectStatusSchema.extract([
  ProjectStatusEnum.awarded,
  ProjectStatusEnum.pre_production,
  ProjectStatusEnum.pre_production_complete,
  ProjectStatusEnum.production,
  ProjectStatusEnum.post_production,
  ProjectStatusEnum.post_production_complete,
  ProjectStatusEnum.processing_final_files,
  ProjectStatusEnum.processing_final_files_complete,
  ProjectStatusEnum.complete,
])

export const isProjectAwarded = <Project extends ProjectDomainSelectModel>(
  project: Project
) => {
  return (
    isProjectWithStatus(project, AwardedProjectStatusSchema.options) &&
    isDefined(project.agencyTeamId)
  )
}

export const PostAwardProjectStatusSchema = ProjectStatusSchema.extract([
  ProjectStatusEnum.pre_production,
  ProjectStatusEnum.pre_production_complete,
  ProjectStatusEnum.production,
  ProjectStatusEnum.post_production,
  ProjectStatusEnum.post_production_complete,
  ProjectStatusEnum.processing_final_files,
  ProjectStatusEnum.processing_final_files_complete,
  ProjectStatusEnum.complete,
])

export const PostProductionCompleteProjectStatuses: NonEmptyArray<ProjectStatus> =
  [
    ProjectStatusEnum.post_production_complete,
    ProjectStatusEnum.processing_final_files,
    ProjectStatusEnum.processing_final_files_complete,
    ProjectStatusEnum.complete,
  ]

/**
 * ProjectMetadata
 * JSON blob for project-level metadata
 */
export const ProjectQfaiMetadataSchema = z.object({
  projectId: NonEmptyStringSchema(),
})

export type ProjectQfaiMetadata = ZodInfer<typeof ProjectQfaiMetadataSchema>

export const ProjectMetadataSchema = z
  .object({
    qfai: ProjectQfaiMetadataSchema,
  })
  .partial()

export type ProjectMetadata = ZodInfer<typeof ProjectMetadataSchema>

const ProjectInheritedPackagePropertiesSchema = z.object({
  name: NameSchema,
  description: DescriptionSchema,
  translations: PackageTranslationsSchema.optional(),
  cost: PackagePayoutSchema,
  costPlusMargin: DollarsSchema(),
  source: PackageSourceSchema,
  format: PackageFormatSchema,
})

/**
 * ProjectDomainSelectModel
 */
export const ProjectDomainSelectModelSchema = z.object({
  projectId: ProjectIdSchema,
  projectKey: ProjectKeySchema,
  projectUrn: ProjectUrnSchema,
  origin: DataOriginSchema.optional(),
  name: NameSchema,
  description: NonEmptyStringSchema().optional(),
  dueDate: DateSchema.optional(),
  ownerId: UserIdSchema,
  brandOrganizationId: OrganizationIdSchema,
  brandTeamId: TeamIdSchema,
  acceptorId: UserIdSchema.optional(),
  agencyOrganizationId: OrganizationIdSchema.optional(),
  agencyTeamId: TeamIdSchema.optional(),
  status: ProjectStatusSchema,
  thumbnailFileId: FileIdSchema.optional(),
  packageId: PackageIdSchema,
  inherited: z.object({
    package: ProjectInheritedPackagePropertiesSchema,
  }),
  metadata: ProjectMetadataSchema.optional(),
  costMarginPercent: CostMarginPercentSchema,
  preProductionReviewRounds: ReviewRoundCountSchema.optional(),
  postProductionReviewRounds: ReviewRoundCountSchema.optional(),
  biddingCloseDate: DateSchema.optional(),
  billingSchedule: BillingScheduleSchema,
  invoiceTerms: InvoiceBillingTermsSchema,
  billingMethods: z.array(ChargeMethodSchema),
  chosenBillingMethod: NoneableChargeMethodSchema,
  currency: CurrencySchema,
  paymentCompletedAt: DateSchema.optional(),
  bidCap: DollarsSchema().optional(),
  bidRecommendation: DollarsSchema().optional(),
})

export type ProjectDomainSelectModel = ZodInfer<
  typeof ProjectDomainSelectModelSchema
>

export function isProjectWithStatus<
  Project extends ProjectDomainSelectModel,
  Status extends ProjectStatus,
>(
  project: Project,
  status: SingleOrArray<Status>
): project is Project & { status: Status } {
  return asArray(status).includes(project.status as Status)
}

/**
 * ProjectViewDomainSelectModelSchema
 */
export const ProjectViewDomainSelectModelSchema =
  ProjectDomainSelectModelSchema.extend({
    lastTouchedTimestamp: TimestampSchema,
    brandCompanyName: CompanyNameSchema,
    cost: DollarsSchema(),
    costPlusMargin: DollarsSchema(),
  })

export type ProjectViewDomainSelectModel = ZodInfer<
  typeof ProjectViewDomainSelectModelSchema
>

/**
 * ProjectDomainInsertModel
 */
export const ProjectDomainInsertModelSchema =
  ProjectDomainSelectModelSchema.omit({
    projectKey: true,
    projectUrn: true,
    thumbnailFileId: true,
    origin: true,
    bidCap: true,
    bidRecommendation: true,
  })
    .partial({
      projectId: true,
    })
    .extend({
      costMarginPercent: CostMarginPercentSchema,
      inherited: ProjectDomainSelectModelSchema.shape.inherited.extend({
        package: ProjectInheritedPackagePropertiesSchema.omit({
          costPlusMargin: true,
        }),
      }),
    })

export type ProjectDomainInsertModel = ZodInfer<
  typeof ProjectDomainInsertModelSchema
>

/**
 * ProjectDomainUpdateModel
 */
export const ProjectDomainUpdateModelSchema =
  ProjectDomainSelectModelSchema.omit({
    projectId: true,
    projectKey: true,
    projectUrn: true,
    origin: true,
    packageId: true,
    inherited: true,
    costMarginPercent: true,
  })
    .extend({
      dueDate: ProjectDomainSelectModelSchema.shape.dueDate.nullable(),
      description: ProjectDomainSelectModelSchema.shape.description,
      thumbnailFileId:
        ProjectDomainSelectModelSchema.shape.thumbnailFileId.nullable(),
      biddingCloseDate:
        ProjectDomainSelectModelSchema.shape.biddingCloseDate.nullable(),
      bidCap: ProjectDomainSelectModelSchema.shape.bidCap.nullable(),
      bidRecommendation:
        ProjectDomainSelectModelSchema.shape.bidRecommendation.nullable(),
    })
    .partial()

export type ProjectDomainUpdateModel = ZodInfer<
  typeof ProjectDomainUpdateModelSchema
>

/**
 * ProjectDomainSelectModelFilter
 */
export const ProjectDomainSelectModelFiltersSchema = z
  .object({
    projectId: ModelFilterSchema(ProjectIdSchema),
    projectKey: ModelFilterSchema(ProjectKeySchema),
    projectUrn: ModelFilterSchema(ProjectUrnSchema),
    packageId: ModelFilterSchema(PackageIdSchema),
    ownerId: ModelFilterSchema(UserIdSchema),
    brandTeamId: ModelFilterSchema(TeamIdSchema),
    acceptorId: ModelFilterSchema(UserIdSchema),
    agencyTeamId: ModelFilterSchema(TeamIdSchema),
    status: ModelFilterSchema(ProjectStatusSchema),
    search: NonEmptyStringSchema(),
    billingMethod: ModelFilterSchema(NoneableChargeMethodSchema),
    tags: TagFilterSchema,
    paymentCompletedAt: z.union([DateSchema, z.literal('undefined')]),
    origin: ModelFilterSchema(DataOriginSchema),
    organizationId: ModelFilterSchema(OrganizationIdSchema),
    brandOrganizationId: ModelFilterSchema(OrganizationIdSchema),
    agencyOrganizationId: ModelFilterSchema(OrganizationIdSchema),
  })
  .partial()

export type ProjectDomainSelectModelFilters = ZodInfer<
  typeof ProjectDomainSelectModelFiltersSchema
>

export const ProjectWorkflowBehaviorSchema = z.object({
  autoSelectTopBid: z.boolean(),
  minBiddingWindowDuration: z.number(),
  maxBiddingWindowDuration: z.number().optional(),
  requireBillingProjectStatus: ProjectStatusSchema.extract([
    'bidding_open',
    'awarded',
  ]),
  forceBidCap: z.boolean(),
})

export type ProjectWorkflowBehavior = ZodInfer<
  typeof ProjectWorkflowBehaviorSchema
>

export const getProjectWorkflowBehavior = (
  // biome-ignore lint/correctness/noUnusedFunctionParameters: This will get used very soon. This should unblock the code of dynamic behavior relying on qfai.
  packageSource: PackageSource
): ProjectWorkflowBehavior => ({
  autoSelectTopBid: false,
  minBiddingWindowDuration: 72,
  maxBiddingWindowDuration: undefined,
  requireBillingProjectStatus: 'awarded',
  forceBidCap: false,
})
